(function (root, factory) {
  const api = factory();
  if (typeof module === 'object' && module.exports) module.exports = api;
  else {
    if (root.RealmFitQuiz) return;
    root.RealmFitQuiz = api;
    const start = () => {
      api.runtime = api.mount(root.document, root);
      const pending = root.RealmFitQuizPendingRecommendation;
      if (pending && root.document.getElementById(pending.ownerId)?.querySelector('script[data-product-json]')) api.runtime.receiveSize(pending.size, pending.productId);
      delete root.RealmFitQuizPendingRecommendation;
    };
    if (root.document.readyState === 'loading') root.document.addEventListener('DOMContentLoaded', start, { once: true });
    else start();
    api.receiveSize = (size, productId) => api.runtime?.receiveSize(size, productId) || false;
    api.propertiesFor = (form, productId) => api.runtime?.propertiesFor(form, productId) || {};
  }
})(typeof window === 'undefined' ? globalThis : window, function () {
  'use strict';

  const sizeNames = new Set(['size', 'taille', 'taglia', 'misura', 'talla', 'størrelse', 'maat', 'größe', 'grösse', 'storlek', 'skostorlek']);
  const sameId = (a, b) => a !== null && a !== undefined && b !== null && b !== undefined && String(a) === String(b);
  const sizeText = size => typeof size === 'string' && size.length <= 80 && !/[\u0000-\u001f\u007f]/.test(size) ? size.trim() : '';

  function nativeState(nativePresent, buttonReady, supported, expired, externalReady = false, additionalReady = false) {
    if ((buttonReady || additionalReady) && supported !== false) return 'ready';
    if (externalReady && supported !== false) return 'external';
    if (!nativePresent || supported === false) return 'unavailable';
    return expired ? 'error' : 'loading';
  }

  function additionalTriggerReady(document, window, productId) {
    return Boolean(document.body?.classList?.contains('es-loaded') && sameId(window.easysize_attributes?.product_id, productId) && window.EasySizeParametersDebug?.product?.supported !== false);
  }

  function guideContextOwned(entry, window) {
    if (!entry?.owner || entry.owner.isConnected === false || entry.guide?.isConnected === false || !sameId(window.easysize_attributes?.product_id, entry.product?.id)) return false;
    try { return sameId(JSON.parse(entry.owner.querySelector('script[data-product-json]').textContent).id, entry.product.id); } catch (_) { return false; }
  }

  function resolveRecommendation(product, size, selected = [], preorderBlocked = false) {
    const text = sizeText(size);
    if (!product || !text || !Array.isArray(product.options) || !Array.isArray(product.variants)) return { ready: false, reason: 'unmapped', size: text };
    const index = product.options.findIndex(option => sizeNames.has(String(typeof option === 'object' ? option.name : option).toLowerCase()));
    if (index < 0) return { ready: false, reason: 'unmapped', size: text };
    const matches = product.variants.filter(variant => Array.isArray(variant.options) && variant.options[index] === text);
    if (!matches.length) return { ready: false, reason: 'unavailable', size: text, index };
    const choices = product.options.map((_, optionIndex) => optionIndex === index ? text : selected[optionIndex] ?? null);
    const complete = choices.every(value => typeof value === 'string' && value.length);
    const variant = complete ? matches.find(item => choices.every((value, optionIndex) => item.options[optionIndex] === value)) : null;
    if (preorderBlocked) return { ready: false, reason: 'preorder', size: text, index, variant: variant || null };
    if (!matches.some(item => item.available === true)) return { ready: false, reason: 'unavailable', size: text, index };
    if (!complete) return { ready: false, reason: 'chooseOptions', size: text, index };
    if (!variant || variant.available !== true) return { ready: false, reason: 'unavailable', size: text, index, variant: variant || null };
    return { ready: true, size: text, index, variant };
  }

  function nativeProperties(form, productId, activeProductId) {
    if (!form || !sameId(productId, activeProductId) || form.isConnected === false) return {};
    const inputs = Array.from(form.querySelectorAll?.('input[name="properties[_esid]"]') || []);
    const input = inputs.find(node => (!node.form || node.form === form) && (!node.type || node.type === 'hidden') && !node.disabled && typeof node.value === 'string' && node.value.length > 0 && node.value.length <= 2048 && !/[\u0000-\u001f\u007f]/.test(node.value));
    return input ? { _esid: input.value } : {};
  }

  class RecommendationSession {
    constructor() { this.generation = 0; this.context = null; this.recommendation = null; }
    begin(owner, productId) {
      this.generation += 1; this.context = { owner, productId: String(productId), generation: this.generation }; this.recommendation = null;
      return this.context;
    }
    receive(context, product, size, selected, preorderBlocked) {
      if (!context || context !== this.context || context.generation !== this.generation || context.owner?.isConnected === false || !sameId(product?.id, context.productId)) return null;
      this.recommendation = resolveRecommendation(product, size, selected, preorderBlocked);
      return this.recommendation;
    }
    end(context = this.context) {
      if (context !== this.context) return false;
      this.generation += 1; this.context = null; this.recommendation = null; return true;
    }
  }

  function changeView(guide, view, document, focus = false) {
    const value = view === 'quiz' ? 'quiz' : 'chart';
    const panel = guide.querySelector('[data-realm-fit-panel]');
    const chartNodes = Array.from(guide.querySelectorAll('[data-realm-fit-chart],[data-guide-body] > .realm-guide-diagram,[data-guide-body] > .realm-guide-table-wrap'));
    if (!panel || !chartNodes.length) return false;
    const body = guide.querySelector('[data-guide-body]');
    if (value === 'quiz' && body && !guide._realmChartScrollSaved) { guide._realmChartScrollSaved = true; guide._realmChartScroll = body.scrollTop; }
    guide.dataset.realmFitView = value;
    for (const node of chartNodes) { node.hidden = value !== 'chart'; if (node.hidden) node.setAttribute('aria-hidden', 'true'); else node.removeAttribute('aria-hidden'); }
    panel.hidden = value !== 'quiz';
    for (const tab of guide.querySelectorAll('[data-realm-fit-tab]')) {
      const active = tab.dataset.realmFitTab === value;
      tab.setAttribute('aria-selected', String(active)); tab.tabIndex = active ? 0 : -1;
      if (active && focus) tab.focus?.({ preventScroll: true });
    }
    for (const units of guide.querySelectorAll('.realm-guide-units')) units.hidden = value !== 'chart';
    if (body) {
      if (value === 'chart' && guide._realmChartScrollSaved) { body.scrollTop = guide._realmChartScroll; guide._realmChartScrollSaved = false; }
    }
    return true;
  }

  function visiblePortal(node, window) {
    if (!node || node.isConnected === false || node.hidden || node.getAttribute?.('aria-hidden') === 'true') return false;
    for (let current = node; current && current.nodeType !== 9; current = current.parentElement) {
      if (current.hidden || current.getAttribute?.('aria-hidden') === 'true') return false;
      const style = window.getComputedStyle?.(current);
      if (style && (style.display === 'none' || style.visibility === 'hidden' || style.visibility === 'collapse')) return false;
    }
    return Boolean(node.getClientRects?.().length);
  }

  function createPortalWatch(document, window, selector, onClose, onTimeout, timeout = 15000) {
    let sawOpen = false, ended = false;
    const finish = reason => {
      if (ended) return; ended = true; observer?.disconnect(); window.clearTimeout(timer);
      if (reason === 'closed') onClose(); else if (reason === 'timeout') onTimeout();
    };
    const inspect = () => {
      if (ended) return;
      let nodes = [];
      try { nodes = Array.from(document.querySelectorAll(selector)); } catch (_) { finish('timeout'); return; }
      const open = nodes.some(node => visiblePortal(node, window));
      if (open) sawOpen = true;
      else if (sawOpen) finish('closed');
    };
    const observer = typeof window.MutationObserver === 'function' ? new window.MutationObserver(inspect) : null;
    const timer = window.setTimeout(() => { if (!sawOpen) finish('timeout'); }, timeout);
    observer?.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['class', 'style', 'hidden', 'aria-hidden'] });
    inspect();
    return { inspect, stop: () => finish('cancelled'), get sawOpen() { return sawOpen; } };
  }

  function createExternalReturn(document, window, caller, selector, isCurrent, onEnd) {
    const position = { left: window.scrollX || 0, top: window.scrollY || 0 }, pathname = window.location?.pathname;
    let valid = true;
    const stillOwned = () => valid && caller?.isConnected !== false && isCurrent() && window.location?.pathname === pathname && !document.querySelector?.('dialog[open]');
    const watch = createPortalWatch(document, window, selector, () => {
      const finish = () => {
        if (stillOwned()) { window.scrollTo?.({ ...position, behavior: 'instant' }); caller.focus?.({ preventScroll: true }); }
        if (valid) onEnd?.(false);
        valid = false;
      };
      if (window.requestAnimationFrame) window.requestAnimationFrame(finish); else window.setTimeout(finish, 0);
    }, () => { if (valid) onEnd?.(true); valid = false; });
    return { stop() { valid = false; watch.stop(); }, get sawOpen() { return watch.sawOpen; } };
  }

  function mount(document, window) {
    const guides = new Map(), sessions = new WeakMap(); let active = null;
    const texts = panel => ({ loading: panel.dataset.fitLoading || '', unavailable: panel.dataset.fitUnavailable || '', error: panel.dataset.fitError || '', external: panel.dataset.fitExternal || '', recommendation: panel.dataset.fitRecommendation || '', chooseOptions: panel.dataset.fitChooseOptions || '', preorder: panel.dataset.fitPreorder || '' });
    const status = (entry, message) => { const node = entry.panel.querySelector('[data-realm-fit-status]'); if (node && node.textContent !== message) node.textContent = message; };
    const ownerFor = panel => document.getElementById(panel.dataset.fitOwner || '') || document.querySelector('.realm-product[data-realm-product]');
    const productFor = owner => { try { return JSON.parse(owner.querySelector('script[data-product-json]').textContent); } catch (_) { return null; } };
    const selectedFor = owner => {
      const product = productFor(owner); return (product?.options || []).map((_, index) => Array.from(owner.querySelectorAll('[data-option-index]')).find(control => Number(control.dataset.optionIndex) === index && (control.type !== 'radio' || control.checked))?.value ?? null);
    };
    const showRecommendation = entry => {
      const recommendation = entry.session.recommendation, node = entry.panel.querySelector('[data-realm-fit-recommendation]'), button = entry.panel.querySelector('[data-realm-fit-use-size]');
      if (node) { const message = recommendation ? texts(entry.panel).recommendation.replace('__SIZE__', recommendation.size) : ''; if (node.hidden !== !recommendation) node.hidden = !recommendation; if (node.textContent !== message) node.textContent = message; }
      if (button) { if (button.hidden !== !recommendation) button.hidden = !recommendation; if (button.disabled !== !recommendation?.ready) button.disabled = !recommendation?.ready; if (button.getAttribute('aria-disabled') !== String(button.disabled)) button.setAttribute('aria-disabled', String(button.disabled)); }
      if (recommendation && !recommendation.ready) status(entry, texts(entry.panel)[recommendation.reason] || texts(entry.panel).unavailable);
      else if (recommendation) status(entry, entry.panel.dataset.fitState === 'external' ? texts(entry.panel).external : '');
    };
    function register(scope = document) {
      for (const [guide, entry] of guides) if (guide.isConnected === false) { if (entry === active) { entry.watch?.stop(); active = null; } entry.observer?.disconnect(); window.clearTimeout(entry.timeout); entry.session.end(); guides.delete(guide); }
      const panels = Array.from(scope.querySelectorAll?.('[data-realm-fit-panel]') || []);
      for (const panel of panels) {
        const guide = panel.closest('dialog'), owner = ownerFor(panel), product = owner && productFor(owner);
        if (!guide || !owner || !sameId(product?.id, panel.dataset.fitProductId) || guides.has(guide)) continue;
        const session = new RecommendationSession(), context = session.begin(owner, product.id), entry = { guide, panel, owner, product, session, context, handoff: null, watch: null };
        guides.set(guide, entry); sessions.set(owner, session);
        const refresh = () => {
          const native = panel.querySelector('[data-realm-fit-native]'), cta = native?.querySelector('#easysize_button.easysize_button');
          const style = cta && window.getComputedStyle?.(cta);
          const ready = Boolean(cta && !cta.hidden && (!style || style.display !== 'none' && style.visibility !== 'hidden'));
          const supported = sameId(window.easysize_attributes?.product_id, product.id) ? window.EasySizeParametersDebug?.product?.supported : undefined;
          const externalCta = owner.querySelector('#easysize_button.easysize_button');
          const externalReady = sameId(window.easysize_attributes?.product_id, product.id) && visiblePortal(externalCta, window);
          const additional = panel.querySelector('[data-realm-fit-launch]');
          const additionalReady = Boolean(additional && additionalTriggerReady(document, window, product.id) && guideContextOwned(entry, window));
          if (additional) {
            if (additional.hidden !== !additionalReady) additional.hidden = !additionalReady;
            if (additional.disabled !== !additionalReady) additional.disabled = !additionalReady;
            if (additional.getAttribute('aria-disabled') !== String(!additionalReady)) additional.setAttribute('aria-disabled', String(!additionalReady));
          }
          const nextState = nativeState(Boolean(native?.querySelector('.easysize-container')), ready, supported, entry.expired, externalReady, additionalReady);
          if (panel.dataset.fitState !== nextState) panel.dataset.fitState = nextState;
          const note = panel.querySelector('[data-realm-fit-handoff-note]'); if (note && note.hidden !== (nextState !== 'ready')) note.hidden = nextState !== 'ready';
          if (!session.recommendation) status(entry, panel.dataset.fitState === 'ready' ? '' : texts(panel)[panel.dataset.fitState]);
          else showRecommendation(entry);
        };
        entry.observer = typeof window.MutationObserver === 'function' ? new window.MutationObserver(refresh) : null;
        entry.observer?.observe(panel, { childList: true, subtree: true, attributes: true, attributeFilter: ['class', 'style', 'hidden'] });
        entry.observer?.observe(owner, { childList: true, subtree: true, attributes: true, attributeFilter: ['class', 'style', 'hidden'] });
        entry.refresh = refresh;
        entry.timeout = window.setTimeout(() => { entry.expired = true; refresh(); }, 15000);
        refresh();
      }
    }
    function beginHandoff(entry, caller) {
      if (!entry.guide.open || active || !guideContextOwned(entry, window)) return false;
      const bridge = window.RealmAppBridge, selector = entry.panel.dataset.fitPortalSelector;
      if (!bridge?.suspendGuide || !bridge?.resumeGuide || !selector) { status(entry, texts(entry.panel).error); return false; }
      const token = bridge.suspendGuide(entry.guide, caller);
      if (!token) { status(entry, texts(entry.panel).error); return false; }
      active = entry; entry.handoff = token;
      const resume = error => {
        if (active !== entry || !entry.handoff) return;
        const saved = entry.handoff; entry.handoff = null; entry.watch = null; active = null;
        if (error) status(entry, texts(entry.panel).error);
        bridge.resumeGuide(saved);
      };
      entry.watch = createPortalWatch(document, window, selector, () => resume(false), () => resume(true));
      return true;
    }
    function beginExternalHandoff(entry, caller) {
      if (active || entry.guide.open || caller.closest?.('[data-realm-fit-native]')) return false;
      const selector = entry.panel.dataset.fitPortalSelector;
      if (!selector || !sameId(window.easysize_attributes?.product_id, entry.product.id)) return false;
      active = entry; entry.external = true;
      entry.watch = createExternalReturn(document, window, caller, selector, () => active === entry && entry.external && entry.owner.isConnected !== false && sameId(window.easysize_attributes?.product_id, entry.product.id), error => {
        if (active !== entry || !entry.external) return;
        entry.watch = null; entry.external = false; active = null;
        if (error) status(entry, texts(entry.panel).error);
      });
      return true;
    }
    function receiveSize(size, productId) {
      const entry = active || Array.from(guides.values()).find(item => item.owner.isConnected !== false && (productId === undefined || sameId(item.context.productId, productId)));
      if (!entry) return false;
      if (productId !== undefined && !sameId(entry.context.productId, productId)) return false;
      const current = productFor(entry.owner);
      const recommendation = entry.session.receive(entry.context, current, size, selectedFor(entry.owner), entry.owner.dataset.preorderPending === 'true');
      if (!recommendation) return false;
      showRecommendation(entry);
      document.dispatchEvent(new window.CustomEvent('realm:fit-recommendation', { detail: { owner: entry.owner, productId: current.id, size: recommendation.size, available: recommendation.ready, deliberateSelectionRequired: true } }));
      return false;
    }
    document.addEventListener('click', event => {
      const tab = event.target.closest?.('[data-realm-fit-tab]');
      if (tab) { event.preventDefault(); changeView(tab.closest('dialog'), tab.dataset.realmFitTab, document); return; }
      const use = event.target.closest?.('[data-realm-fit-use-size]');
      if (!use) return;
      event.preventDefault(); const entry = guides.get(use.closest('dialog'));
      if (!entry || entry.owner.matches?.('[data-realm-card-product]')) return;
      const product = productFor(entry.owner), previous = entry.session.recommendation;
      if (!previous || !sameId(product?.id, entry.context.productId)) return;
      const recommendation = resolveRecommendation(product, previous.size, selectedFor(entry.owner), entry.owner.dataset.preorderPending === 'true');
      entry.session.recommendation = recommendation; showRecommendation(entry);
      if (!recommendation.ready) return;
      const control = Array.from(entry.owner.querySelectorAll('input[data-option-index]')).find(node => Number(node.dataset.optionIndex) === recommendation.index && node.value === recommendation.size && !node.disabled);
      if (!control) return;
      control.click(); changeView(entry.guide, 'chart', document, true);
    });
    document.addEventListener('click', event => {
      if (active?.external && event.target.closest?.('[data-dialog-open],[data-cart-open],a[href]')) { active.watch?.stop(); active.external = false; active = null; }
      const additional = event.target.closest?.('[data-realm-fit-launch].easysize-widget');
      const nativeCta = additional || event.target.closest?.('[data-realm-fit-native] #easysize_button.easysize_button');
      if (!nativeCta) {
        const outside = event.target.closest?.('#easysize_button.easysize_button');
        const entry = outside && Array.from(guides.values()).find(item => item.owner.contains(outside));
        if (entry) beginExternalHandoff(entry, outside);
        return;
      }
      const entry = guides.get(nativeCta.closest('dialog'));
      if (!entry || (additional && !additionalTriggerReady(document, window, entry.product.id)) || !beginHandoff(entry, nativeCta)) { event.preventDefault(); event.stopImmediatePropagation(); }
    }, true);
    document.addEventListener('keydown', event => {
      const caller = event.target.closest?.('#easysize_button.easysize_button');
      if (!caller || event.key !== 'Enter') return;
      const entry = Array.from(guides.values()).find(item => item.owner.contains(caller));
      if (entry) beginExternalHandoff(entry, caller);
    }, true);
    document.addEventListener('keydown', event => {
      const tab = event.target.closest?.('[data-realm-fit-tab]');
      if (!tab || !['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
      event.preventDefault(); changeView(tab.closest('dialog'), event.key === 'Home' ? 'chart' : event.key === 'End' ? 'quiz' : tab.dataset.realmFitTab === 'chart' ? 'quiz' : 'chart', document, true);
    });
    document.addEventListener('change', event => {
      const owner = event.target.closest?.('[data-realm-product]');
      if (!owner) return;
      const entry = Array.from(guides.values()).find(item => item.owner === owner);
      if (entry?.session.recommendation) { entry.session.receive(entry.context, productFor(owner), entry.session.recommendation.size, selectedFor(owner), owner.dataset.preorderPending === 'true'); showRecommendation(entry); }
    });
    document.addEventListener('shopify:section:load', event => register(event.target));
    const interrupt = () => { if (!active) return; active.watch?.stop(); active.handoff = null; active.external = false; active = null; };
    document.addEventListener('realm:fit-handoff-interrupted', interrupt);
    document.addEventListener('realm:overlay-opening', () => { if (active?.external) interrupt(); });
    window.addEventListener('pagehide', interrupt);
    const globalObserver = typeof window.MutationObserver === 'function' ? new window.MutationObserver(() => { for (const entry of guides.values()) entry.refresh(); }) : null;
    globalObserver?.observe(document.body, { attributes: true, attributeFilter: ['class'] });
    globalObserver?.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
    register();
    return { receiveSize, register, propertiesFor(form, productId) { const owner = form?.closest?.('[data-realm-product]'); const session = owner && sessions.get(owner); return nativeProperties(form, productId, session?.context?.productId); }, beginHandoff };
  }

  return { nativeState, additionalTriggerReady, guideContextOwned, resolveRecommendation, nativeProperties, RecommendationSession, changeView, visiblePortal, createPortalWatch, createExternalReturn, mount };
});
